import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useBreadcrumbs } from '../../contexts/BreadcrumbContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { getResume, ResumeData } from '../../services/resume';
import {
  deleteSavedJob,
  listSavedJobs,
  saveJobPosition,
  tailorResumeWithLLM,
} from '../../services/resumeTuner';
import {
  CustomizedResume,
  SavedJobPosition,
} from '../../types/resumeTuning';
import { MatchAnalysisPanel } from '../../components/ResumeTuner/MatchAnalysisPanel';
import { ComparisonView } from '../../components/ResumeTuner/ComparisonView';
import './ResumeTuner.css';

type Step = 'job' | 'generating' | 'results';

export function ResumeTuner() {
  const { user } = useAuth();
  const { setPageCrumbs } = useBreadcrumbs();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  // Pre-filled from Resume Hub "Retune" action
  const navState = (location.state || {}) as {
    jobTargetId?: string;
    jobTitle?: string;
    company?: string;
    jobDescription?: string;
  };
  const incomingJobTargetId = navState.jobTargetId || '';

  const [currentStep, setCurrentStep] = useState<Step>('job');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const [baseResume, setBaseResume] = useState<ResumeData | null>(null);
  const [savedJobs, setSavedJobs] = useState<SavedJobPosition[]>([]);

  const [jobTitle, setJobTitle] = useState(navState.jobTitle || '');
  const [company, setCompany] = useState(navState.company || '');
  const [jobDescription, setJobDescription] = useState(navState.jobDescription || '');
  const [tone, setTone] = useState<'technical' | 'executive' | 'balanced'>('balanced');
  const [userNotes, setUserNotes] = useState('');

  const [selectedMode, setSelectedMode] = useState<'quick' | 'thorough'>('quick');
  const [modelTier, setModelTier] = useState<'standard' | 'premium'>('standard');
  const [generatingResume, setGeneratingResume] = useState(false);
  const [generateElapsed, setGenerateElapsed] = useState(0);
  const elapsedTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [generatedResume, setGeneratedResume] = useState<CustomizedResume | null>(null);
  const [savingJob, setSavingJob] = useState(false);

  useEffect(() => {
    loadData();
  }, [user]);

  useEffect(() => {
    setPageCrumbs([{ label: t('nav.resumeTuner') }]);
  }, [setPageCrumbs, t]);

  const loadData = async () => {
    setLoading(true);
    const [resumeResult, jobsResult] = await Promise.allSettled([getResume(), listSavedJobs()]);
    if (resumeResult.status === 'fulfilled' && resumeResult.value?.data) {
      setBaseResume(resumeResult.value.data);
    }
    if (jobsResult.status === 'fulfilled') {
      setSavedJobs(Array.isArray(jobsResult.value) ? jobsResult.value : []);
    }
    setLoading(false);
  };

  const handleSaveCurrentJob = async () => {
    if (!jobTitle || !company || !jobDescription) {
      setError('Please fill in job title, company, and description before saving.');
      return;
    }
    try {
      setSavingJob(true);
      setError('');
      const savedJob = await saveJobPosition({ title: jobTitle, company, description: jobDescription });
      setSavedJobs((prev) => [savedJob, ...prev.filter((j) => j.id !== savedJob.id)]);
      setSuccessMessage('Job saved for later.');
    } catch (err: any) {
      setError(err.message || 'Failed to save job.');
    } finally {
      setSavingJob(false);
    }
  };

  const handleSelectSavedJob = (job: SavedJobPosition) => {
    setJobTitle(job.title);
    setCompany(job.company);
    setJobDescription(job.description);
    setSuccessMessage('Job loaded.');
  };

  const handleDeleteSavedJob = async (id: string) => {
    try {
      await deleteSavedJob(id);
      setSavedJobs((prev) => prev.filter((j) => j.id !== id));
    } catch (err: any) {
      setError(err.message || 'Failed to delete saved job.');
    }
  };

  const handleGenerate = async (mode: 'quick' | 'thorough') => {
    if (!baseResume) {
      setError(t('resumeTuner.noBaseResume'));
      return;
    }
    if (!jobTitle || !company || !jobDescription) {
      setError('Please fill in the job title, company, and job description.');
      return;
    }

    try {
      setSelectedMode(mode);
      setGeneratingResume(true);
      setGenerateElapsed(0);
      setError('');
      setSuccessMessage('');
      setCurrentStep('generating');

      // Start elapsed timer for deep mode step progress
      if (elapsedTimerRef.current) clearInterval(elapsedTimerRef.current);
      elapsedTimerRef.current = setInterval(() => {
        setGenerateElapsed((s) => s + 1);
      }, 1000);

      const result = await tailorResumeWithLLM({
        jobTitle,
        company,
        jobDescription,
        tone,
        userNotes: userNotes || undefined,
        mode,
        modelTier: mode === 'thorough' ? modelTier : 'standard',
        jobTargetId: incomingJobTargetId || undefined,
      });

      setGeneratedResume(result);
      if (incomingJobTargetId) {
        navigate('/saved-resumes');
        return;
      }
      setCurrentStep('results');
    } catch (err: any) {
      console.error('Failed to tailor resume:', err);
      setError(err.message || 'Failed to generate tailored resume. Please try again.');
      setCurrentStep('job');
    } finally {
      setGeneratingResume(false);
      if (elapsedTimerRef.current) {
        clearInterval(elapsedTimerRef.current);
        elapsedTimerRef.current = null;
      }
    }
  };

  const handleStartOver = () => {
    setCurrentStep('job');
    setJobTitle('');
    setCompany('');
    setJobDescription('');
    setUserNotes('');
    setGeneratedResume(null);
    setError('');
    setSuccessMessage('');
    setGenerateElapsed(0);
    if (elapsedTimerRef.current) {
      clearInterval(elapsedTimerRef.current);
      elapsedTimerRef.current = null;
    }
  };

  return (
    <div className="resume-tuner-container">
      <div className="resume-tuner-header">
        {incomingJobTargetId && jobTitle ? (
          <>
            <h1>Retune Resume</h1>
            <p className="subtitle">
              Adding a new version for <strong>{jobTitle}</strong>{company ? ` @ ${company}` : ''}. Edit details below then generate.
            </p>
          </>
        ) : (
          <>
            <h1>{t('resumeTuner.title')}</h1>
            <p className="subtitle">
              Paste a job description — the AI reads your career memory and crafts a tailored resume with an honest match analysis.
            </p>
          </>
        )}
      </div>

      {error && (
        <div className="alert alert-error">
          <button type="button" className="alert-close" onClick={() => setError('')} aria-label="Close">×</button>
          <strong>{t('common.error')}:</strong> {error}
        </div>
      )}

      {successMessage && (
        <div className="alert alert-success">
          <button type="button" className="alert-close" onClick={() => setSuccessMessage('')} aria-label="Close">×</button>
          {successMessage}
        </div>
      )}

      {loading && !baseResume && (
        <div className="alert alert-info resume-loading-notice">
          Loading your resume profile…
        </div>
      )}

      {!loading && !baseResume && (
        <div className="alert alert-error">
          No resume found.{' '}
          <button type="button" className="btn-link" onClick={() => navigate('/resume')}>
            Set up your resume
          </button>{' '}
          before tailoring.
        </div>
      )}

      <div className="resume-tuner-content">

        {/* ── Step 1: Job Details ── */}
        {currentStep === 'job' && (
          <div className="job-input-step">
            <div className="job-form-section">
              <h2>Target Job</h2>

              {savedJobs.length > 0 && (
                <div className="saved-jobs-bar">
                  <span className="saved-jobs-label">Saved jobs:</span>
                  <div className="saved-jobs-chips">
                    {savedJobs.map((job) => (
                      <div key={job.id} className="saved-job-chip">
                        <button
                          type="button"
                          className="saved-job-chip-label"
                          onClick={() => handleSelectSavedJob(job)}
                          title={`${job.title} at ${job.company}`}
                        >
                          {job.title} @ {job.company}
                        </button>
                        <button
                          type="button"
                          className="saved-job-chip-delete"
                          onClick={() => handleDeleteSavedJob(job.id)}
                          aria-label="Delete saved job"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="jobTitle">Job Title *</label>
                  <input
                    id="jobTitle"
                    type="text"
                    className="form-input"
                    placeholder="e.g. Senior Software Engineer"
                    value={jobTitle}
                    onChange={(e) => setJobTitle(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="company">Company *</label>
                  <input
                    id="company"
                    type="text"
                    className="form-input"
                    placeholder="e.g. Acme Corp"
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="jobDescription">
                  Job Description *
                  <span className="field-hint"> — paste the full posting for best results</span>
                </label>
                <textarea
                  id="jobDescription"
                  className="form-input job-description-textarea"
                  placeholder="Paste the job description here…"
                  value={jobDescription}
                  onChange={(e) => setJobDescription(e.target.value)}
                  rows={12}
                />
              </div>

              <details className="advanced-options">
                <summary>Advanced options</summary>
                <div className="advanced-options-content">
                  <div className="form-group">
                    <label htmlFor="tone">Writing Tone</label>
                    <select
                      id="tone"
                      className="form-input"
                      value={tone}
                      onChange={(e) => setTone(e.target.value as typeof tone)}
                    >
                      <option value="balanced">Balanced (default)</option>
                      <option value="technical">Technical — precise language, stack-focused</option>
                      <option value="executive">Executive — business impact, leadership</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="userNotes">Notes for the AI</label>
                    <textarea
                      id="userNotes"
                      className="form-input"
                      placeholder="e.g. Emphasize my team-lead experience, or downplay the gap in 2022…"
                      value={userNotes}
                      onChange={(e) => setUserNotes(e.target.value)}
                      rows={3}
                    />
                  </div>
                  <div className="form-group">
                    <label>Model Quality <span className="field-hint"> — applies to Deep Tailor only</span></label>
                    <div className="model-tier-toggle">
                      <button
                        type="button"
                        className={`model-tier-btn ${modelTier === 'standard' ? 'model-tier-btn-active' : ''}`}
                        onClick={() => setModelTier('standard')}
                      >
                        Standard
                        <span className="model-tier-hint">Gemini Flash · faster</span>
                      </button>
                      <button
                        type="button"
                        className={`model-tier-btn ${modelTier === 'premium' ? 'model-tier-btn-active' : ''}`}
                        onClick={() => setModelTier('premium')}
                      >
                        Premium
                        <span className="model-tier-hint">Gemini Pro · deeper analysis</span>
                      </button>
                    </div>
                  </div>
                </div>
              </details>

              <div className="job-form-actions">
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={handleSaveCurrentJob}
                  disabled={savingJob || !jobTitle || !company}
                >
                  {savingJob ? 'Saving…' : 'Save job for later'}
                </button>
              </div>

              <div className="mode-buttons">
                <button
                  type="button"
                  className="btn-mode"
                  onClick={() => handleGenerate('quick')}
                  disabled={generatingResume || loading || !baseResume || !jobTitle || !company || !jobDescription}
                >
                  <span className="mode-icon">⚡</span>
                  <span className="mode-label">Quick Tailor</span>
                  <span className="mode-hint">~30s · good quality</span>
                </button>
                <button
                  type="button"
                  className="btn-mode btn-mode-deep"
                  onClick={() => handleGenerate('thorough')}
                  disabled={generatingResume || loading || !baseResume || !jobTitle || !company || !jobDescription}
                >
                  <span className="mode-icon">🔍</span>
                  <span className="mode-label">Deep Tailor</span>
                  <span className="mode-hint">~90s · searches career memory per requirement</span>
                </button>
              </div>

              <p className="ai-note">
                The AI reads your career memory graph, rewrites your bullets to match this job's language, and produces a detailed match analysis — no manual selection needed. Deep mode runs targeted searches for each job requirement for higher quality output.
              </p>
            </div>
          </div>
        )}

        {/* ── Step 2: Generating ── */}
        {currentStep === 'generating' && (
          <div className="generating-step">
            <div className="generating-content">
              <div className="generating-spinner"></div>
              {selectedMode === 'thorough' ? (
                <>
                  <h2>Deep tailoring in progress…</h2>
                  <p>
                    Running a 6-step analysis for <strong>{jobTitle}</strong> at <strong>{company}</strong>.
                    {modelTier === 'premium' && <span className="premium-badge"> · Gemini Pro</span>}
                  </p>
                  <div className="deep-mode-steps">
                    {[
                      { label: 'Analyzing job requirements', doneAt: 8 },
                      { label: 'Searching career memory for evidence', doneAt: 25 },
                      { label: 'Mapping your experience to requirements', doneAt: 60 },
                      { label: 'Building gap intelligence', doneAt: 85 },
                      { label: 'Crafting tailored resume', doneAt: 110 },
                      { label: 'Generating candidate profile', doneAt: 999 },
                    ].map(({ label, doneAt }, i) => {
                      const thresholds = [0, 8, 25, 60, 85, 110];
                      const prevDoneAt = thresholds[i];
                      const isDone = generateElapsed >= doneAt;
                      const isActive = !isDone && generateElapsed >= prevDoneAt;
                      return (
                        <div
                          key={label}
                          className={`deep-step ${isDone ? 'deep-step-done' : isActive ? 'deep-step-active' : 'deep-step-pending'}`}
                        >
                          <span className="deep-step-icon">{isDone ? '✓' : isActive ? '⟳' : '○'}</span>
                          <span>{label}</span>
                        </div>
                      );
                    })}
                  </div>
                  <p className="generating-hint">Deep tailoring typically takes 90–120 seconds.</p>
                </>
              ) : (
                <>
                  <h2>Tailoring your resume…</h2>
                  <p>
                    The AI is reading your career memory, selecting relevant experiences, and crafting targeted bullets for{' '}
                    <strong>{jobTitle}</strong> at <strong>{company}</strong>.
                  </p>
                  <p className="generating-hint">This typically takes 15–60 seconds.</p>
                </>
              )}
            </div>
          </div>
        )}

        {/* ── Step 3: Results ── */}
        {currentStep === 'results' && generatedResume && (
          <>
            <div className="results-header">
              <h2>
                Tailored Resume — {generatedResume.jobPosition.title} at {generatedResume.jobPosition.company}
              </h2>
              {generatedResume.matchAnalysis.aiGenerated && (
                <span className="ai-badge">✨ AI-generated</span>
              )}
            </div>

            <MatchAnalysisPanel matchAnalysis={generatedResume.matchAnalysis} />

            <ComparisonView
              baseResume={baseResume!}
              customizedResume={generatedResume}
              onStartOver={handleStartOver}
            />

            <div className="resume-tuner-saved-notice">
              <span className="resume-tuner-saved-badge">✓ {t('resumeTuner.autoSaved')}</span>
              <button type="button" className="btn btn-secondary" onClick={() => navigate('/saved-resumes')}>
                {t('resumeTuner.viewSavedVersions')}
              </button>
            </div>

            <div className="resume-tuner-interview-cta">
              <button
                type="button"
                className="btn btn-accent"
                onClick={() => navigate('/interview-practice', { state: { jobTitle, company, jobDescription } })}
              >
                {t('resumeTuner.practiceInterview')}
              </button>
              <p className="interview-cta-hint">{t('resumeTuner.practiceInterviewHint')}</p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
